import { ProductInfoComponent, ProductType, ProductIdentifiers, PlannerValidation } from '@inter-ikea-storageone/range';
import { ComponentIds, HierarchyUtils } from '@inter-ikea-core/core';
import { CornerSolutionComponent } from '@inter-ikea-storageone/range';
export class InnerLightPlacementValidation extends PlannerValidation {
    constructor(coreApi) {
        super(coreApi, 'InnerLightPlacementValidation');
        this.diffNorrflyNormal = 80;
        this.diffNorrflyEdgeCase = 330;
        this.diffOversidanNormal = 40;
        this.diffOversidanEdgeCase = 290;
        this.coreApi = coreApi;
    }
    validateCurrentState() {
        return this.validationOK();
    }
    validateSnap(snapActionCandidate) {
        const entity = snapActionCandidate.guestRoot;
        if (!this.getIsInnerLight(entity)) {
            return this.validationOK();
        }
        const hostSize = snapActionCandidate.host.getComponent(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)?.size;
        const entitySize = entity.getComponent(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)?.size;
        if (!hostSize || !entitySize) {
            return this.validationFail('Couldn´t get size');
        }
        // Place first light on T-divider on same side as clothRail
        if (snapActionCandidate.host.getComponent(ProductInfoComponent.id)
            ?.productType === ProductType.PAX_T_DIVIDER) {
            const clothRail = HierarchyUtils.children(snapActionCandidate.host).find(entity => this.coreApi.entities.isOneOfProductTypes(entity, [
                ProductType.PAX_CLOTH_RAIL_580,
                ProductType.PAX_CLOTH_RAIL_350
            ]));
            const innerLight = HierarchyUtils.children(snapActionCandidate.host).find(entity => this.getIsInnerLight(entity));
            if (clothRail && !innerLight) {
                const localPosition = clothRail.getComponent(ComponentIds.LOCAL_TRANSFORM_COMPONENT_ID)?.transform.position;
                if (localPosition &&
                    localPosition.X < 0 &&
                    snapActionCandidate.hostConnector.key === 'light-inner-left' &&
                    snapActionCandidate.guestConnector.key === 'attachment-center') {
                    return this.validationOK();
                }
                else if (localPosition &&
                    localPosition.X >= 0 &&
                    snapActionCandidate.hostConnector.key === 'light-inner-right' &&
                    snapActionCandidate.guestConnector.key === 'attachment-center') {
                    return this.validationOK();
                }
                else {
                    return this.validationFail('Couldn´t place light in T-divider with clothes rail');
                }
            }
            else {
                if (snapActionCandidate.guestConnector.key === 'attachment-center') {
                    return this.validationOK();
                }
                else {
                    return this.validationFail('Couldn´t place light in T-divider');
                }
            }
        }
        const cornerSolution = HierarchyUtils.findWithComponentInUpwardsHierarchy(snapActionCandidate.host, CornerSolutionComponent.id);
        // Place shorter light in normal corner frame on right side
        if (cornerSolution && hostSize.width > 500) {
            const isMirrored = cornerSolution
                .getComponent(ComponentIds.PRODUCT_COMPONENT_ID)
                ?.productRef.includes(ProductIdentifiers.MIRRORED_PRODUCT);
            const connectorKey = isMirrored ? 'attachment-right' : 'attachment-left';
            const diff = this.getIsInnerLightNorrfly(entity)
                ? this.diffNorrflyEdgeCase
                : this.diffOversidanEdgeCase;
            if (snapActionCandidate.guestConnector.key === connectorKey &&
                hostSize.width - entitySize.width === diff) {
                return this.validationOK();
            }
            else {
                return this.validationFail('Couldn´t place light in corner solution');
            }
        }
        if (this.getIsInnerLightNorrfly(entity))
            return this.checkNorrflyConnection(snapActionCandidate, hostSize, entitySize);
        else
            return this.checkOversidanConnection(snapActionCandidate, hostSize, entitySize);
    }
    checkNorrflyConnection(snapActionCandidate, hostSize, entitySize) {
        if (!snapActionCandidate?.host.hasComponent(ComponentIds.FRAME_COMPONENT_ID)) {
            if (snapActionCandidate.guestConnector.key === 'attachment-center' &&
                hostSize.width - entitySize.width === this.diffNorrflyNormal) {
                return this.validationOK();
            }
            else {
                return this.validationFail('Couldn´t place light in regular frame');
            }
        }
        const entityGroup = HierarchyUtils.findWithComponentInUpwardsHierarchy(snapActionCandidate.host, ComponentIds.AUTOMATIC_ENTITY_GROUP_COMPONENT_ID);
        // The frame has sliding doors
        if (entityGroup) {
            if (hostSize.width === 500 &&
                snapActionCandidate.guestConnector.key === 'attachment-offset-center') {
                return this.validationOK();
            }
            else if (hostSize.width - entitySize.width === this.diffNorrflyEdgeCase) {
                return this.validationOK();
            }
            else {
                return this.validationFail('Couldn´t place light in 500 wide frame with sliding doors');
            }
        }
        else {
            if (snapActionCandidate.guestConnector.key === 'attachment-center' &&
                hostSize.width - entitySize.width === this.diffNorrflyNormal) {
                return this.validationOK();
            }
            else {
                return this.validationFail('Couldn´t place light in frame with sliding doors');
            }
        }
    }
    checkOversidanConnection(snapActionCandidate, hostSize, entitySize) {
        const parent = HierarchyUtils.parent(snapActionCandidate.host);
        if (parent) {
            const parentSize = parent.getComponent(ComponentIds.COMMUNICATION_SIZE_COMPONENT_ID)?.size;
            if (this.coreApi.entities.isProductType(snapActionCandidate.host, ProductType.PAX_SHELF) &&
                this.coreApi.entities.isProductType(parent, ProductType.PAX_ADD_ON_CORNER_FRAME) &&
                parentSize?.depth === 580) {
                return this.validationFail('Can not place inner light on add on corner frame shelf');
            }
        }
        if (snapActionCandidate.guestConnector.key === 'attachment-center' &&
            hostSize.width - entitySize.width === this.diffOversidanNormal) {
            return this.validationOK();
        }
        else {
            return this.validationFail('Couldn´t place inner light in frame');
        }
    }
    getIsInnerLight(entity) {
        return this.getIsInnerLightNorrfly(entity) || this.getIsInnerLightOversidan(entity);
    }
    getIsInnerLightNorrfly(entity) {
        return entity.getComponent(ComponentIds.LIGHT_COMPONENT_ID)?.type === 'inner';
    }
    getIsInnerLightOversidan(entity) {
        return (entity.getComponent(ComponentIds.LIGHT_COMPONENT_ID)?.type ===
            'inner-oversidan');
    }
}
//# sourceMappingURL=InnerLightPlacementValidation.js.map